import React from 'react';
import { translate } from 'react-jhipster';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

export interface IRebateReportSetting {
  enabled: boolean;
  columns: string[];
}

export const emptyReportSetting: IRebateReportSetting = { enabled: false, columns: [] };

// Order matches the "4. OPTIONAL REPORT COLUMNS TO SEND" mockup, left column then right column.
export const REPORT_COLUMNS: string[] = [
  'referenceNumber',
  'customerName',
  'customerId',
  'commodity',
  'shmokhIncentive',
  'email',
  'quotationNumber',
  'date',
  'bankName',
  'branchName',
  'bankStaffName',
  'currency',
  'bankAIncentive',
  'quantity',
  'amount',
  'fee',
  'status',
  'thirdPartyIncentive',
  'pendingAmount',
];

export const isReportSettingValid = (value: IRebateReportSetting) => !value.enabled || value.columns.length > 0;

/**
 * Reads the report setting saved for a bank/commodityType from the JSON-encoded `rebateReportSettings*` column.
 */
export const reportSettingFromBank = (settingsJson?: string | null): IRebateReportSetting => {
  if (settingsJson) {
    try {
      const parsed = JSON.parse(settingsJson);
      if (parsed && typeof parsed.enabled === 'boolean' && Array.isArray(parsed.columns)) {
        return { enabled: parsed.enabled, columns: parsed.columns.filter(c => REPORT_COLUMNS.includes(c)) };
      }
    } catch {
      // not valid JSON; fall through to the default below
    }
  }
  return emptyReportSetting;
};

/** One row of the Excel report generated from this setting (ALSHMOKH-26) — field names match {@code REPORT_COLUMNS} 1:1, mirroring the backend's {@code RebateReportRowDTO}. */
export interface IRebateReportRow {
  referenceNumber?: string;
  customerName?: string;
  customerId?: string;
  commodity?: string;
  shmokhIncentive?: number;
  email?: string;
  quotationNumber?: number;
  date?: string;
  bankName?: string;
  branchName?: string;
  bankStaffName?: string;
  currency?: string;
  bankAIncentive?: number;
  quantity?: number;
  amount?: number;
  fee?: number;
  status?: string;
  thirdPartyIncentive?: number;
  // ALSHMOKH-70: set only for a carry-forward row, e.g. "Amount was pending in April 2026 and
  // confirmed in May 2026." Not a selectable column: it's added whenever a row has a note.
  pendingNote?: string;
}

const NUMERIC_REPORT_COLUMNS = ['shmokhIncentive', 'bankAIncentive', 'quantity', 'amount', 'fee', 'thirdPartyIncentive'];

/** A report row's value for one `REPORT_COLUMNS` key, ready to drop into an Excel cell. */
export const columnValue = (row: IRebateReportRow, column: string): string | number => {
  const fields = row as Record<string, string | number | undefined>;
  if (column === 'date') {
    return row.date ? dayjs(row.date).format('DD/MM/YYYY') : '';
  }
  if (NUMERIC_REPORT_COLUMNS.includes(column)) {
    return Number(fields[column] ?? 0);
  }
  return fields[column] ?? '';
};

interface ReportSectionProps {
  value: IRebateReportSetting;
  onChange: (value: IRebateReportSetting) => void;
  showErrors: boolean;
  bankName: string;
}

export const ReportSection = ({ value, onChange, showErrors, bankName }: ReportSectionProps) => {
  const error = showErrors && !isReportSettingValid(value);

  const toggleColumn = (column: string) => {
    const columns = value.columns.includes(column) ? value.columns.filter(c => c !== column) : [...value.columns, column];
    onChange({ ...value, columns });
  };

  return (
    <Box sx={{ maxWidth: { md: '75%' } }}>
      <FormControlLabel
        control={<Switch checked={value.enabled} onChange={e => onChange({ ...value, enabled: e.target.checked })} />}
        label={translate('comexApp.bank.rebate.report.enable')}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(value.enabled ? 'comexApp.bank.rebate.report.enabledHint' : 'comexApp.bank.rebate.report.disabledHint')}
      </Typography>

      {value.enabled && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {translate('comexApp.bank.rebate.report.columnsLabel')}
          </Typography>
          <FormGroup>
            <Grid container spacing={0}>
              {REPORT_COLUMNS.map(column => (
                <Grid item xs={12} sm={6} key={column}>
                  <FormControlLabel
                    control={<Checkbox checked={value.columns.includes(column)} onChange={() => toggleColumn(column)} />}
                    label={translate(`comexApp.bank.rebate.report.columns.${column}`, { bankName })}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
          {error && (
            <Typography variant="caption" color="error" sx={{ mx: 1.75 }}>
              {translate('comexApp.bank.rebate.report.required')}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};
